import {
  ConversationDraftScope,
  ConversationDraftSideEffectActionType,
  SendUnlessConditionType,
} from '@provision/conversation-types';
import { ApolloClient } from '@provision/graphql-client';
import {
  ConversationMessageDraft,
  GetUserConversationMessageDraft,
  isDraftNonEmpty,
  mapDraft,
} from 'src/graphql';
import { GET_CONVERSATION_MESSAGE_DRAFT_QUERY } from 'src/graphql/entities/conversation-message-draft/conversation-message-draft.operations';
import {
  createScheduleFromSource,
  isScheduleEmpty,
} from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { clearLocalDraftForConversation } from 'src/pages/Conversations/Chat/conversation-draft-storage';
import { ConversationMessageDraftRaw } from 'src/types';
import { parseJsonSafely } from 'src/utils/objects';

const OPTIMISTIC_ID_PREFIX = 'optimistic-';

export function isOptimisticDraftId(id: string | null | undefined): boolean {
  return Boolean(id?.startsWith(OPTIMISTIC_ID_PREFIX));
}
export async function findServerDraftIdByScope(
  apolloClient: ApolloClient<unknown>,
  scope: { conversationId: string; isPrivate: boolean; isScheduled: boolean },
): Promise<string | null> {
  const { data } = await apolloClient.query<GetUserConversationMessageDraft>({
    query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY,
    fetchPolicy: 'network-only',
  });

  const match = (data?.getUserConversationMessageDraft?.records ?? [])
    .map(mapDraft)
    .find(
      (draft) =>
        draft.conversationId === scope.conversationId &&
        Boolean(draft.isPrivate) === scope.isPrivate &&
        Boolean(draft.isScheduled) === scope.isScheduled,
    );

  return match?.id ?? null;
}

export function upsertDraftInCache(
  cache: ApolloClient<unknown>['cache'],
  incoming: ConversationMessageDraft,
) {
  const draft = normalizeDraftForCache(incoming);
  const schedule = createScheduleFromSource({
    scheduledAt: draft.scheduledAt ?? null,
    sideEffects: draft.sideEffects,
    sendUnless: draft.sendUnless,
  });
  const scheduleEmpty = isScheduleEmpty(schedule);

  if (!isDraftNonEmpty(draft.message) && scheduleEmpty) {
    clearLocalDraftForConversation(draft.conversationId);
  }

  const keyOf = (d: ConversationMessageDraft | ConversationMessageDraftRaw) =>
    `${d.conversationId}:${d.isPrivate ? 'private' : 'public'}:${determineScope(d)}`;

  cache.updateQuery<GetUserConversationMessageDraft>(
    { query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY },
    (existing) => {
      const prev = existing?.getUserConversationMessageDraft?.records ?? [];
      const idx = prev.findIndex((d) => keyOf(d) === keyOf(draft));

      // insert
      if (idx === -1) {
        return {
          getUserConversationMessageDraft: {
            __typename: 'GetUserConversationMessageDraft',
            // keep any other fields if they existed
            ...(existing?.getUserConversationMessageDraft ?? {}),
            // overriding types to avoid extra data mapping to satisfy ts
            records: [...prev, draft as unknown as ConversationMessageDraftRaw],
          },
        };
      }

      // update only if fresher
      if (!isFresher(draft.updatedAt, prev[idx].updatedAt)) {
        return (
          existing ?? {
            getUserConversationMessageDraft: {
              __typename: 'GetUserConversationMessageDraft',
              records: prev,
            },
          }
        );
      }

      const next = prev.slice();

      const nextItem = {
        ...prev[idx],
        ...draft,
        conversation: prev[idx].conversation ?? draft.conversation,
      } as ConversationMessageDraftRaw;

      next[idx] = nextItem;

      return {
        getUserConversationMessageDraft: {
          __typename: 'GetUserConversationMessageDraft',
          ...(existing?.getUserConversationMessageDraft ??
            ({} as ConversationMessageDraftRaw[])),
          records: next,
        },
      };
    },
    { broadcastAll: true },
  );
}

function isFresher(a?: string, b?: string) {
  return (a ? Date.parse(a) : 0) >= (b ? Date.parse(b) : 0);
}

function normalizeDraftForCache(incoming: ConversationMessageDraft): ConversationMessageDraft {
  const scheduledAt = incoming.scheduledAt ?? null;
  const sideEffects = normalizeSideEffects(incoming.sideEffects);
  const sendUnless = normalizeSendUnless(incoming.sendUnless);
  const discardedAt = incoming.discardedAt ?? null;

  const draft: ConversationMessageDraft = {
    ...incoming,
    __typename: 'ConversationMessageDraft',
    scheduledAt,
    sideEffects,
    sendUnless,
    discardedAt,
    isScheduled: incoming.isScheduled ?? Boolean(scheduledAt || sideEffects || sendUnless),
    conversation: {
      __typename: 'Conversation',
      id: incoming.conversation?.id ?? incoming.conversationId,
      conversationId: incoming.conversation?.conversationId ?? incoming.conversationId,
      subject: incoming.conversation?.subject ?? null,
      workspaceConversationId: incoming.conversation?.workspaceConversationId ?? null,
      conversationStatusId: incoming.conversation?.conversationStatusId ?? null,
    } as ConversationMessageDraft['conversation'],
  };

  return draft;
}

function determineScope(
  draft: ConversationMessageDraft | ConversationMessageDraftRaw,
): ConversationDraftScope {
  if ('isScheduled' in draft) {
    return draft.isScheduled
      ? ConversationDraftScope.Scheduled
      : ConversationDraftScope.Unscheduled;
  }

  const schedule = createScheduleFromSource({
    scheduledAt: draft.scheduledAt ?? null,
    sideEffects: normalizeSideEffects(draft.sideEffects),
    sendUnless: normalizeSendUnless(draft.sendUnless),
  });

  return isScheduleEmpty(schedule)
    ? ConversationDraftScope.Unscheduled
    : ConversationDraftScope.Scheduled;
}

function normalizeSideEffects(
  input: ConversationMessageDraft['sideEffects'] | string | null | undefined,
): ConversationMessageDraft['sideEffects'] | null {
  if (!input) {
    return null;
  }

  const parsed =
    typeof input === 'string'
      ? (parseJsonSafely<{ actions?: unknown }>(input) ?? null)
      : input;

  if (!isRecord(parsed)) {
    return null;
  }

  const rawActions = Array.isArray(parsed.actions) ? parsed.actions : [];
  const actions: NonNullable<ConversationMessageDraft['sideEffects']>['actions'] = [];

  for (const action of rawActions) {
    if (!isRecord(action)) {
      continue;
    }
    if (!isSendAndActionType(action.type)) {
      continue;
    }

    const value = action.value === 'string' ? action.value : null;

    actions.push({
      __typename: 'ConversationMessageDraftSendAndAction',
      type: action.type,
      value,
    });
  }

  if (!actions.length) {
    return null;
  }

  return {
    __typename: 'ConversationMessageDraftSendAnd',
    actions,
  };
}

function normalizeSendUnless(
  input: ConversationMessageDraft['sendUnless'] | string | null | undefined,
): ConversationMessageDraft['sendUnless'] | null {
  if (!input) {
    return null;
  }

  const parsed =
    typeof input === 'string' ? (parseJsonSafely<unknown[]>(input) ?? null) : input;

  if (!Array.isArray(parsed)) {
    return null;
  }

  const normalized: NonNullable<ConversationMessageDraft['sendUnless']> = [];

  for (const item of parsed) {
    if (!isRecord(item)) {
      continue;
    }
    if (!isSendUnlessType(item.type)) {
      continue;
    }

    const value = typeof item.value === 'string' ? item.value : null;

    normalized.push({
      __typename: 'ConversationMessageDraftSendUnless',
      type: item.type,
      value,
    });
  }

  return normalized.length ? normalized : null;
}

function isSendAndActionType(
  value: unknown,
): value is NonNullable<ConversationMessageDraft['sideEffects']>['actions'][number]['type'] {
  return (
    value === ConversationDraftSideEffectActionType.Snooze ||
    value === ConversationDraftSideEffectActionType.Status
  );
}

const SEND_UNLESS_CONDITION_TYPES = [
  SendUnlessConditionType.AgentReply,
  SendUnlessConditionType.ClientReply,
  SendUnlessConditionType.AssigneeChange,
  SendUnlessConditionType.StatusChange,
];

function isSendUnlessType(value: unknown): value is SendUnlessConditionType {
  return SEND_UNLESS_CONDITION_TYPES.includes(value as SendUnlessConditionType);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
